import React, { useEffect, useState } from "react";

import moment from "moment";

import { getGraphDataPointsInterval } from "utils/utils";
import { CHART_DOWNLOAD_DATA_DIALOG_STRINGS, InputErrorStrings, valueStrings, WidgetStrings } from "constants/constantStrings";
import { DATE_TIME_SECONDS_FORMAT, HOUR_IN_SECONDS, INPUT_DEBOUNCE_DURATION, LOCAL_TZ_CODE, MIN_IN_SECONDS } from "constants/globalConstants";

import BadgeLabel from "components/BadgeLabel";
import Dropdown from "components/Dropdown";
import DupontDialog, { DIALOG_ACTIONS } from "components/DupontDialog";
import InputBox from "components/InputBox";

import "./index.scss";

const DataDownloadDialog = props => {
  const [timeInterval, setTimeInterval] = useState();
  const [timeUnit, setTimeUnit] = useState();
  const [inputError, setInputError] = useState();
  const [unitsError, setUnitsError] = useState();
  const [downloadDurationMsg, setDownloadDurationMsg] = useState(null);
  const [defaultInterval, setDefaultInterval] = useState(0);

  const TIME_UNITS = [valueStrings.seconds, valueStrings.minutes, valueStrings.hours];
  const unitInSeconds = {
    [valueStrings.seconds]: 1,
    [valueStrings.minutes]: MIN_IN_SECONDS,
    [valueStrings.hours]: HOUR_IN_SECONDS
  };

  useEffect(() => {
    const { startTime, endTime } = props.DurationRanges;
    if (startTime && endTime) {
      const startDate = moment(startTime, "X").format(DATE_TIME_SECONDS_FORMAT);
      const endDate = moment(endTime, "X").format(DATE_TIME_SECONDS_FORMAT);
      const message = CHART_DOWNLOAD_DATA_DIALOG_STRINGS.downloadDurationMsg
        .replace("[from]", startDate)
        .replace("[to]", `${endDate} ${LOCAL_TZ_CODE}`);
      setDownloadDurationMsg(message);
    }
  }, [props.DurationRanges]);

  useEffect(() => {
    const defaultInterval = getGraphDataPointsInterval(props.DurationRanges);
    let unit = TIME_UNITS[0];
    let interval = defaultInterval;
    if (defaultInterval >= HOUR_IN_SECONDS && defaultInterval % HOUR_IN_SECONDS === 0) {
      interval = defaultInterval / HOUR_IN_SECONDS;
      unit = TIME_UNITS[2];
    } else if (defaultInterval >= MIN_IN_SECONDS && defaultInterval % MIN_IN_SECONDS === 0) {
      interval = defaultInterval / MIN_IN_SECONDS;
      unit = TIME_UNITS[1];
    }
    setDefaultInterval(defaultInterval);
    setTimeInterval(interval);
    setTimeUnit(unit);
  }, [props.Duration]);

  useEffect(() => {
    setInputError(null);
    const inputValidation = setTimeout(() => {
      if (timeInterval !== "" && timeUnit === valueStrings.seconds && timeInterval < 10) {
        setInputError(CHART_DOWNLOAD_DATA_DIALOG_STRINGS.minimumValMsg);
      }
    }, INPUT_DEBOUNCE_DURATION);
    return () => clearTimeout(inputValidation);
  }, [timeInterval, timeUnit]);

  const onDialogClose = action => {
    let dataInterval;
    if (action === DIALOG_ACTIONS.CONFIRM) {
      const interval = timeInterval * unitInSeconds[timeUnit];
      if (defaultInterval !== interval) {
        dataInterval = { interval };
      } else {
        dataInterval = {};
      }
    }
    props.onDialogClose(dataInterval, downloadDurationMsg);
  };

  const inputChangeHanlder = val => setTimeInterval(val);

  const dropdownChangeHandler = val => {
    setTimeUnit(val);
    const timeUnitError = !val ? InputErrorStrings.required : null;
    setUnitsError(timeUnitError);
  };

  return (
    <DupontDialog
      open
      title={CHART_DOWNLOAD_DATA_DIALOG_STRINGS.title}
      cancelBtnText={valueStrings.cancel}
      confirmBtnText={CHART_DOWNLOAD_DATA_DIALOG_STRINGS.confirmBtn}
      customClass="download-data-dailog--width"
      confirmDisabled={!timeInterval || !timeUnit || inputError}
      onClose={onDialogClose}
      maxWidth="725px"
    >
      <div className="download-data-dailog-main">
        <BadgeLabel badgeText={downloadDurationMsg} customClass="download-duration--msg" />
        {CHART_DOWNLOAD_DATA_DIALOG_STRINGS.dialogDescription}
        <div className="download-csv-input">
          <label className="dupont-form-label" htmlFor="tags-time-interval">
            {CHART_DOWNLOAD_DATA_DIALOG_STRINGS.intervalLabel}*
          </label>
          <div className="input-container">
            <InputBox
              customClass="input-box"
              name="tags-time-interval"
              type="number"
              value={timeInterval}
              errorMsg={inputError}
              requiredMsg={InputErrorStrings.required}
              placeholder={WidgetStrings.typeHere}
              onChange={inputChangeHanlder}
            />

            <Dropdown customClass="time-unit" value={timeUnit} options={TIME_UNITS} errorMsg={unitsError} onChange={dropdownChangeHandler} />
          </div>
        </div>
      </div>
    </DupontDialog>
  );
};

export default DataDownloadDialog;
